import React from 'react';
import ReactEcharts from 'echarts-for-react';
import { toast } from 'react-toastify';
import ColorPicker from 'src/components/Colorpicker/index-beta';
import { useChartManagement } from 'src/managers/chartManager/stateManagement/management';
import ImperativeZoomButton from '../../../../ImperativeZoomButton';
import { withZoom } from '../../../echarts/util/zoom/zoomableEchart';
import './styles.css';

/**
 * GraphBubbleChart de correlação.
 *
 * Segue o mesmo contrato dos demais gráficos integrados ao ChartManager:
 *   - `this.manager` orquestra ciclo de cores, highlit e color picker
 *   - `this.colorPickerRef` aponta para o componente do color picker
 *   - `this.configurationError` carrega o erro vindo do ThemeManager
 *
 * O `dataProcessor` continua a mesma — quem alinha as categorias (0..N-1) à lista
 * canônica de perguntas correlacionadas é o `toEchartOption`, para que o pipeline
 * de cor por pergunta funcione idêntico aos demais gráficos da correlação.
 */
class GraphBubbleChart extends React.Component {
  constructor(props) {
    super(props);
    const { error } = this.props;
    this.configurationError = error;
    this.colorPickerRef = React.createRef();
    this.zoomButton = React.createRef();
    this.manager = useChartManagement(this);
  }

  shouldComponentUpdate(nextProps) {
    return this.manager.changesChecker(nextProps);
  }

  // Caso a api retorne um erro no fetch das configurações de cores, não será possível abrir
  // o color picker e uma mensagem de erro será apresentada ao usuário.
  onConfigurationError() {
    if (!this.configurationError) return;
    toast.error(this.configurationError.toString());
  }

  onChartReady(echarts) {
    this.echarts = withZoom(echarts);
  }

  handleZoomIn() {
    if (!this.echarts) return;
    const currentZoom = this.echarts.getCurrentZoom();
    if (currentZoom === 1 && this.zoomButton.current) {
      this.zoomButton.current.enableZoomOut();
    }
    this.echarts.incrementZoom(1);
  }

  handleZoomOut() {
    if (!this.echarts) return;
    this.echarts.incrementZoom(-1);
    const afterIncrement = this.echarts.getCurrentZoom();
    if (afterIncrement === 1 && this.zoomButton.current) {
      this.zoomButton.current.disableZoomOut();
    }
  }

  render() {
    const { colorPickerColors, chartRef, theme } = this.props;
    const options = this.manager.getOptions();

    if (!options || !options.series) {
      return null;
    }

    return (
      <div
        style={{ position: 'relative', width: '100%', height: '100%' }}
        className="zoomable"
      >
        <ReactEcharts
          option={options}
          onChartReady={(echarts) => this.onChartReady(echarts)}
          ref={(echartsRef) => {
            if (chartRef) chartRef.current = echartsRef;
            this.chartRef = echartsRef;
          }}
          onEvents={{
            dblclick: (ctx) => this.manager.handleOnDoubleClick(ctx),
            legendselectchanged: (ctx) => this.manager.handleOnLegendChange(ctx),
          }}
        />
        <ImperativeZoomButton
          ref={this.zoomButton}
          style={{ position: 'absolute', right: 0, bottom: 0 }}
          theme={theme && theme.correlation && theme.correlation.zoom}
          onZoomIn={() => this.handleZoomIn()}
          onZoomOut={() => this.handleZoomOut()}
          disableZoomOut
        />
        <ColorPicker
          ref={this.colorPickerRef}
          hideColorPicker={() => this.manager.colorPickerHandler.hideColorPicker()}
          changeBarColor={(newColor) => this.manager.colorPickerHandler.changeColorHandler(newColor)}
          colors={colorPickerColors}
        />
      </div>
    );
  }
}

export default GraphBubbleChart;
